import React, { useState } from 'react';
import { PlusOutlined } from '@ant-design/icons';
import { CustomDynamicSelect } from '@/components';
import SearchInput from '@/components/SearchInput';
import { DeleteIcon } from '@/assets/icons/icons';
import type { PickerCatalogProps } from '../productPickerCatalog';
import type { EditProductLine } from '../editProductLines';
import { ProductSerialInput, QuantityInput } from './ProductQuantityRows';
import styles from './EditProductRows.module.css';

interface EditProductRowsProps {
  lines: EditProductLine[];
  onLinesChange: (lines: EditProductLine[]) => void;
  onProductIdsChange: (ids: string[]) => void;
  getProductName: (id: string) => string;
  catalog: PickerCatalogProps;
}

/** The edit drawer's product grid: a product dropdown, quantity, serials and delete per line, and "Add product" (Figma 45930:22752). */
const EditProductRows: React.FC<EditProductRowsProps> = ({
  lines,
  onLinesChange,
  onProductIdsChange,
  getProductName,
  catalog,
}) => {
  const [draftKeys, setDraftKeys] = useState<number[]>([]);
  const [nextDraftKey, setNextDraftKey] = useState(0);
  const lineIds = lines.map((line) => line.id);

  const update = (id: string, patch: Partial<EditProductLine>): void => {
    onLinesChange(
      lines.map((line) => (line.id === id ? { ...line, ...patch } : line))
    );
  };

  const addDraft = (): void => {
    setDraftKeys((keys) => [...keys, nextDraftKey]);
    setNextDraftKey((key) => key + 1);
  };

  const removeDraft = (key: number): void => {
    setDraftKeys((keys) => keys.filter((draftKey) => draftKey !== key));
  };

  const productSelect = (
    value: string | undefined,
    onSelect: (id: string) => void,
    ariaLabel: string
  ): React.ReactNode => {
    const options =
      value && !catalog.options.some((option) => option.id === value)
        ? [{ id: value, name: getProductName(value) }, ...catalog.options]
        : catalog.options;
    return (
      <CustomDynamicSelect
        aria-label={ariaLabel}
        placeholder='Select product'
        width='100%'
        height='36px'
        border='0px'
        bgColor='var(--light-gray)'
        textColor='var(--text-primary)'
        borderRadius='8px'
        options={options.map((option) => ({
          id: option.id,
          name: option.name,
          disabled: option.id !== value && lineIds.includes(option.id),
        }))}
        label='name'
        value={value}
        onChange={(id) => onSelect(String(id))}
        onPopupScroll={catalog.onScroll}
        onDropdownVisibleChange={(open) => {
          if (!open && catalog.search) catalog.onSearch('');
        }}
        dropdownRender={(menu) => (
          <>
            <div className={styles.search}>
              <SearchInput
                key={`edit-product-search-${catalog.searchInputKey}`}
                placeholder='Search product'
                width='100%'
                defaultValue={catalog.search}
                onChange={catalog.onSearch}
                onClear={() => catalog.onSearch('')}
              />
            </div>
            {menu}
          </>
        )}
      />
    );
  };

  const deleteButton = (label: string, onClick: () => void) => (
    <button
      type='button'
      className={styles.action}
      aria-label={label}
      onClick={onClick}
    >
      <DeleteIcon />
    </button>
  );

  return (
    <div className={styles.table}>
      {(lines.length > 0 || draftKeys.length > 0) && (
        <div className={`${styles.row} ${styles.header}`}>
          <span className={styles.name}>Name</span>
          <span className={styles.quantity}>Qty</span>
          <span className={styles.serials}>Serials</span>
          <span className={styles.action} />
        </div>
      )}
      {lines.map((line) => {
        const name = getProductName(line.id);
        return (
          <div key={line.id} className={styles.row}>
            <span className={styles.name}>
              {productSelect(
                line.id,
                (id) =>
                  onProductIdsChange(
                    lineIds.map((lineId) => (lineId === line.id ? id : lineId))
                  ),
                `Product for ${name}`
              )}
            </span>
            <span className={styles.quantity}>
              <QuantityInput
                value={line.quantity}
                onCommit={(quantity) => update(line.id, { quantity })}
                width='48px'
                ariaLabel={`Quantity for ${name}`}
              />
            </span>
            <span className={styles.serials}>
              <ProductSerialInput
                value={line.serial_number}
                onChange={(serial_number) => update(line.id, { serial_number })}
                ariaLabel={`Serials for ${name}`}
              />
            </span>
            {deleteButton(`Delete ${name}`, () =>
              onProductIdsChange(lineIds.filter((id) => id !== line.id))
            )}
          </div>
        );
      })}
      {draftKeys.map((key) => (
        <div key={`draft-${key}`} className={styles.row}>
          <span className={styles.name}>
            {productSelect(
              undefined,
              (id) => {
                removeDraft(key);
                onProductIdsChange([...lineIds, id]);
              },
              'New product'
            )}
          </span>
          <span className={styles.quantity} />
          <span className={styles.serials} />
          {deleteButton('Delete new product row', () => removeDraft(key))}
        </div>
      ))}
      <button type='button' className={styles.add} onClick={addDraft}>
        <span className={styles.addIcon}>
          <PlusOutlined />
        </span>
        Add product
      </button>
    </div>
  );
};

export default EditProductRows;
